import { getV1JWTWithoutRefresh } from '../util/jwt_helper'
import BaseService from './BaseService'
import store from '@/store'

class OauthService extends BaseService {
  private defaultAxiosInstance
  private options
  constructor(baseUrl: string) {
    super(baseUrl, true)
    this.defaultAxiosInstance = this.axiosInstance
    this.axiosInstance = this.secondaryInstance
    this.options = {
      headers: this.headers,
    }
  }

  loginWithEmail = (body: { [key: string]: any }, url = 'email') =>
    this.requests.post(`/2/login/${url}`, body, this.options)
  forgotPassword = (body: { email: string; companyId?: string }) => this.requests.post(`/users/forgotPassword`, body)
  changePassword = (body: {
    userId?: string
    newPassword: string
    token: string
    companyId?: string
    setToAll?: boolean
  }) => {
    if (!body.userId) {
      throw 'UserId needed'
    }
    return this.requests.post(`/users/changePassword`, body, this.options)
  }
  validateForgotPasswordLink = (body: { userId: string; token: string; companyId: string }) => {
    if (!body.userId) {
      throw 'UserId needed'
    }
    return this.requests.get(
      `/users/forgotPassword/validateLink?token=${body.token}&companyId=${body.companyId}&userId=${body.userId}`
    )
  }
  refreshToken = async (refresh_token: string, newRefreshToken?: string) => {
    return this.noJWTInstance({
      url: `${this.baseUrl}/2/login/token`,
      method: 'POST',
      headers: { ...this.headers, 'refresh-token': refresh_token },
      data: { refreshTokenV2: newRefreshToken },
    }).then(this.responseBody)
  }
  refreshJwt = async (refresh_token: string) => {
    return this.axiosInstance({
      url: `${this.baseUrl}/refresh`,
      method: 'POST',
      headers: { ...this.headers, 'refresh-token': refresh_token },
    }).then(this.responseBody)
  }
  loginAsProxy = (userId: string) => {
    this.axiosInstance = this.defaultAxiosInstance
    const response = this.requests.post(`/2/login/proxy`, { userId }, this.options)
    this.axiosInstance = this.secondaryInstance
    return response
  }
  getCurrentUser = async () => {
    const deviceId = await store.dispatch('getDeviceId')
    this.axiosInstance = this.defaultAxiosInstance
    const response = this.requests.get(`/2/login/current`, {
      headers: {
        'device-id': deviceId,
        ...this.headers,
      },
    })
    this.axiosInstance = this.secondaryInstance
    return response
  }

  async getSettings(companyId: string) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.get(`/whitelabel/${companyId}`)
    this.axiosInstance = this.secondaryInstance

    return response
  }

  async getCustomPages(companyId: string, locationId?: string) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.get(
      `/sidenav-custom-pages?companyId=${companyId}${locationId ? `&locationId=${locationId}` : ''}`
    )
    this.axiosInstance = this.secondaryInstance

    return response
  }

  async getUserSessionJWT(appId: string, locationId?: string) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.get(`/sso/${appId}/session-details`, {
      headers: {
        ...(locationId && { location: locationId }),
      },
    })
    this.axiosInstance = this.secondaryInstance

    return response
  }

  async signout(sessionId: string) {
    try {
      // We are explicitly using the old JWT here
      // The signout endpoint is currently not acting on the jwt v2
      // Do not send jwt v2 to the signout endpoint until the backend changes are done
      await this.noJWTInstance.post(
        `/signout`,
        {},
        {
          headers: {
            ...this.headers,
            'session-id': sessionId,
            authorization: `Bearer ${getV1JWTWithoutRefresh()}`,
          },
        }
      )
    } catch (error) {
      console.error('Error signing out', error)
    }
  }

  async signinRefresh(activeLocations) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.post(`/2/login/signin/refresh?version=2&location_id=${activeLocations}`, {})
    this.axiosInstance = this.secondaryInstance
    return response
  }

  async getSSOConfigByEmail(email: string) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.get(`/sso/agency/config?email=${email}`)
    this.axiosInstance = this.secondaryInstance
    return response
  }

  async getSSOConfigByCompanyId(companyId: string) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.get(`/sso/agency/config?companyId=${companyId}`)
    this.axiosInstance = this.secondaryInstance
    return response
  }

  // Get access token from code & state (companyId)
  async getAccessToken(payload: {
    code: string
    companyId: string
    deviceId: string
    deviceType: string
    deviceName: string
  }) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.post(`/sso/agency/login`, payload)
    this.axiosInstance = this.secondaryInstance
    return response
  }

  async getKey(locationId: string) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.get(`/keys/location/${locationId}`)
    this.axiosInstance = this.secondaryInstance
    return response
  }

  async getCannySSOLoginToken(userId: string) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.get(`/2/login/canny?userId=${userId}`, {
      headers: {
        ...this.headers,
      },
    })
    this.axiosInstance = this.secondaryInstance
    return response
  }

  async getListOfIntegrations(requestParams: Record<string, any>) {
    this.axiosInstance = this.defaultAxiosInstance
    const response = await this.requests.get('/integration', {
      params: requestParams,
    })
    this.axiosInstance = this.secondaryInstance
    return response
  }

  async loginForSupportAgent(payload: {
    userId: string
    secret: string
    supportAgentId: string
    deviceId?: string
    deviceName?: string
    deviceType?: string
  }) {
    const response = await this.noJWTInstance({
      url: this.baseUrl + `/2/login/supportAgent`,
      method: 'POST',
      data: payload,
    }).then(this.responseBody)

    return response
  }
}

const instance = new OauthService(BaseService.servicesConfig.Oauth)

export default instance
